import type mapboxgl from "mapbox-gl";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "syngenta-digital-cropwise-react-ui-kit";
import { formatTranslation } from "../../../../shared/utils/translationUtils";
import { setBaseStylePreservingLayers } from "../../../utils/mapStyleState";
import { BASE_LAYERS, type BaseLayerType } from "../MapControlsPanel.types";

interface BaseLayerSelectorProps {
  mapRef: mapboxgl.Map | null;
  onLayerChange?: (layerId: string) => void;
}

/** Map a Mapbox style name/sprite onto one of our four base layer options. */
export const baseLayerIdFromStyleName = (
  styleIdentifier: string
): string | undefined => {
  const name = styleIdentifier.toLowerCase();

  if (!name) {
    return undefined;
  }
  // satellite-streets must be tested before the broader satellite match.
  if (
    name.includes("satellite-streets") ||
    name.includes("satellite streets")
  ) {
    return "hybrid";
  }
  if (name.includes("satellite")) {
    return "satellite";
  }
  if (name.includes("streets")) {
    return "streets";
  }
  if (name.includes("outdoors") || name.includes("terrain")) {
    return "terrain";
  }
  return undefined;
};

const BaseLayerSelector: React.FC<BaseLayerSelectorProps> = ({
  mapRef,
  onLayerChange,
}) => {
  const { t } = useTranslation();
  const [selectedLayer, setSelectedLayer] = useState<string>("satellite");

  /** Translated layer name, falling back to the English constant. */
  const layerName = (layer: BaseLayerType): string =>
    formatTranslation(t(`newTrial.mapControls.baseLayers.${layer.id}`)) ||
    layer.name;

  useEffect(() => {
    if (!mapRef) {
      return;
    }

    const syncSelectedLayer = (): void => {
      try {
        const style = mapRef.getStyle();
        const matchedId = baseLayerIdFromStyleName(
          style?.name ?? style?.sprite ?? ""
        );
        if (matchedId) {
          setSelectedLayer(matchedId);
        }
      } catch (error) {
        console.warn("Failed to detect map style:", error);
      }
    };

    syncSelectedLayer();

    // Other tools swap the base style behind our back (Road names promotes a
    // pure satellite style to satellite-streets), so keep the highlight in sync.
    mapRef.on("styledata", syncSelectedLayer);

    return () => {
      try {
        mapRef.off("styledata", syncSelectedLayer);
      } catch {
        // Expected while the map is unmounting.
      }
    };
  }, [mapRef]);

  const handleLayerChange = (layer: BaseLayerType): void => {
    if (!mapRef) {
      return;
    }

    setSelectedLayer(layer.id);
    // Snapshots the trial design and replays it once the new style has loaded.
    setBaseStylePreservingLayers(mapRef, layer.style);
    onLayerChange?.(layer.id);
  };

  return (
    <div className="base-layer-selector">
      <div className="base-layer-grid">
        {BASE_LAYERS.map((layer) => (
          <Tooltip key={layer.id} title={layerName(layer)} placement="top">
            <button
              type="button"
              className={`base-layer-option${
                selectedLayer === layer.id ? " active" : ""
              }`}
              onClick={() => {
                handleLayerChange(layer);
              }}
              aria-label={layerName(layer)}
              aria-pressed={selectedLayer === layer.id}
              data-testid={`base-layer-${layer.id}`}
            >
              <img
                className="base-layer-thumbnail"
                src={layer.thumbnail}
                alt={layerName(layer)}
              />
            </button>
          </Tooltip>
        ))}
      </div>
    </div>
  );
};

export default BaseLayerSelector;
